import React, { useEffect, useMemo, useState } from 'react';
import vkBridge from '@vkontakte/vk-bridge';
import { Root, Snackbar, Spinner, SplitCol, SplitLayout, View } from '@vkontakte/vkui';
import { useFirstPageCheck, useGetPanelForView, useParams, useRouteNavigator, useSearchParams } from '@vkontakte/vk-mini-apps-router';
import {
  ACHIEVEMENTS_PANEL,
  CABINET_PANEL,
  CATEGORY_PANEL,
  DAILY_PANEL,
  DEFAULT_VIEW,
  DICTIONARY_PANEL,
  HOME_PANEL,
  RATING_PANEL,
  TRAINING_PANEL,
  WORD_PANEL
} from './routing/routing';
import * as api from './services/api';
import { PrimaryButton } from './components/Buttons/Buttons';
import { AchievementModal } from './components/AchievementModal/AchievementModal';
import { AppShell } from './components/Layout/AppShell';
import { LoadingBlock } from './components/States/States';
import { AchievementsPanel } from './panels/Achievements/AchievementsPanel';
import { CabinetPanel } from './panels/Cabinet/CabinetPanel';
import { CategoryPanel } from './panels/Category/CategoryPanel';
import { DailyPanel } from './panels/Daily/DailyPanel';
import { DictionaryPanel } from './panels/Dictionary/DictionaryPanel';
import { HomePanel } from './panels/Home/HomePanel';
import { RatingPanel } from './panels/Rating/RatingPanel';
import { TrainingPanel } from './panels/Training/TrainingPanel';
import { WordPanel } from './panels/Word/WordPanel';
import { ensureArray, localizeErrorMessage } from './utils/backend';
import {
  loadCategoryActivity,
  recordCategoryOpened,
  recordCategoryTraining,
  recordCategoryWordViewed,
  saveCategoryScrollPosition
} from './utils/categoryActivity';
import {
  buildCategoryPath,
  buildTrainingPath,
  buildWordPath,
  panelPaths,
  panelToRouteKey,
  parseIntParam,
  resolvePanelPath,
  resolveTrainingFallbackPath,
  resolveWordFallbackPath,
  routeKeys
} from './utils/navigation';

const emptyTraining = { items: [], answers: [], done: false, correct: 0, unlocked: [], source: '', category_id: 0 };
const bridgeUserInfoTimeoutMS = 1500;

function fallbackUserInfo() {
  return {
    first_name: 'Dev',
    last_name: 'User',
    photo_200: ''
  };
}

async function getUserInfo() {
  if (!vkBridge.supports('VKWebAppGetUserInfo')) {
    return fallbackUserInfo();
  }

  try {
    const result = await Promise.race([
      vkBridge.send('VKWebAppGetUserInfo', {}),
      new Promise((_, reject) => setTimeout(() => reject(new Error('VKWebAppGetUserInfo timeout')), bridgeUserInfoTimeoutMS))
    ]);
    return {
      first_name: result?.first_name || '',
      last_name: result?.last_name || '',
      photo_200: result?.photo_200 || ''
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      return fallbackUserInfo();
    }
    throw error;
  }
}

function App() {
  const routeNavigator = useRouteNavigator();
  const activePanel = useGetPanelForView(DEFAULT_VIEW) || HOME_PANEL;
  const isFirstPage = useFirstPageCheck();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [player, setPlayer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryActivity, setCategoryActivity] = useState(() => loadCategoryActivity());
  const [category, setCategory] = useState(null);
  const [categoryWords, setCategoryWords] = useState([]);
  const [word, setWord] = useState(null);
  const [wordInDictionary, setWordInDictionary] = useState(false);
  const [checkingWordStatus, setCheckingWordStatus] = useState(false);
  const [wordBackPanel, setWordBackPanel] = useState(HOME_PANEL);
  const [dailyWords, setDailyWords] = useState([]);
  const [dictionaryWords, setDictionaryWords] = useState([]);
  const [rating, setRating] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [training, setTraining] = useState(emptyTraining);
  const [trainingBackPanel, setTrainingBackPanel] = useState(HOME_PANEL);
  const [selectedTrainingOption, setSelectedTrainingOption] = useState('');
  const [trainingFeedbackPhase, setTrainingFeedbackPhase] = useState('idle');
  const [trainingFeedbackAnswer, setTrainingFeedbackAnswer] = useState(null);
  const [isSubmittingTraining, setIsSubmittingTraining] = useState(false);
  const [trainingSubmitError, setTrainingSubmitError] = useState('');
  const [loading, setLoading] = useState(true);
  const [startupError, setStartupError] = useState('');
  const [toast, setToast] = useState('');
  const [loadedCategoryID, setLoadedCategoryID] = useState(0);
  const [loadedWordID, setLoadedWordID] = useState(0);
  const [loadedTrainingKey, setLoadedTrainingKey] = useState('');
  const [ratingLoaded, setRatingLoaded] = useState(false);
  const [dictionaryLoaded, setDictionaryLoaded] = useState(false);

  const currentTrainingIndex = training.answers.length;
  const currentTrainingItem = training.items[currentTrainingIndex] || null;
  const currentUserRating = useMemo(() => {
    if (!rating?.current_user) {
      return null;
    }
    return rating.current_user;
  }, [rating]);

  useEffect(() => {
    void bootstrap();
  }, []);

  function navigateTo(panel) {
    return routeNavigator.push(panelPaths[panel] || '/');
  }

  function navigateToPath(path) {
    return routeNavigator.push(path);
  }

  function goBack(fallbackPath = '/') {
    if (isFirstPage) {
      return routeNavigator.replace(fallbackPath);
    }

    return routeNavigator.back();
  }

  async function bootstrap() {
    try {
      setLoading(true);
      const profile = await getUserInfo();
      const [playerResp, categoriesResp, dailyResp, achievementsResp, dictionaryResp] = await Promise.all([
        api.syncProfile(profile),
        api.getCategories(),
        api.getDailyWords(),
        api.getAchievements(),
        api.getDictionary()
      ]);
      setPlayer(playerResp.data.response);
      setCategories(ensureArray(categoriesResp.data.response));
      setDailyWords(ensureArray(dailyResp.data.response));
      setAchievements(ensureArray(achievementsResp.data.response));
      setDictionaryWords(ensureArray(dictionaryResp.data.response));
      setDictionaryLoaded(true);
      setStartupError('');
    } catch (err) {
      setStartupError(localizeErrorMessage(err?.response?.data?.error?.message || 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }

  function notify(message) {
    setToast(message);
  }

  function resetTrainingUIState() {
    setSelectedTrainingOption('');
    setTrainingFeedbackPhase('idle');
    setTrainingFeedbackAnswer(null);
    setIsSubmittingTraining(false);
    setTrainingSubmitError('');
  }

  async function ensureCategoryLoaded(categoryId, options = {}) {
    if (!categoryId) {
      return;
    }

    const nextCategory = options.categoryOverride || categories.find((item) => item.id === categoryId) || category;
    if (!options.force && loadedCategoryID === categoryId) {
      if (nextCategory?.id === categoryId) {
        setCategory(nextCategory);
      }
      return;
    }

    if (loadedCategoryID !== categoryId) {
      setCategory(nextCategory?.id === categoryId ? nextCategory : { id: categoryId, name: nextCategory?.name || 'Категория', image: nextCategory?.image || '' });
      setCategoryWords([]);
      setLoadedCategoryID(0);
    }

    if (!options.silent) {
      setLoading(true);
    }
    try {
      const resp = await api.getCategoryWords(categoryId);
      const categoryFromList = options.categoryOverride || categories.find((item) => item.id === categoryId) || nextCategory;
      setCategory(categoryFromList?.id === categoryId ? categoryFromList : { id: categoryId, name: categoryFromList?.name || 'Категория', image: categoryFromList?.image || '' });
      setCategoryWords(ensureArray(resp.data.response));
      setLoadedCategoryID(categoryId);
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  }

  async function ensureWordLoaded(wordId, options = {}) {
    if (!wordId) {
      return;
    }

    if (!options.force && loadedWordID === wordId) {
      if (options.backPanel) {
        setWordBackPanel(options.backPanel);
      }
      return;
    }

    if (options.previewWord) {
      setWord(options.previewWord);
      setLoadedWordID(0);
    } else if (loadedWordID !== wordId) {
      setWord(null);
      setLoadedWordID(0);
    }
    setWordInDictionary(false);
    setLoading(true);
    setCheckingWordStatus(true);
    try {
      const [wordResp, statusResp] = await Promise.all([
        api.getWord(wordId),
        api.getDictionaryStatus(wordId)
      ]);
      setWord(wordResp.data.response);
      setWordInDictionary(Boolean(statusResp.data.response.exists));
      setLoadedWordID(wordId);
      if (options.backPanel) {
        setWordBackPanel(options.backPanel);
      }
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      setCheckingWordStatus(false);
      setLoading(false);
    }
  }

  async function ensureTrainingLoaded(source, categoryId, returnPanel, options = {}) {
    if (!source) {
      return;
    }

    const trainingKey = `${source}:${categoryId || 0}`;
    if (!options.force && loadedTrainingKey === trainingKey) {
      setTrainingBackPanel(returnPanel);
      return;
    }

    setLoading(true);
    try {
      resetTrainingUIState();
      const resp = await api.getTraining({ source, category_id: categoryId || 0 });
      setTraining({ ...emptyTraining, items: ensureArray(resp.data.response), source, category_id: categoryId || 0 });
      setTrainingBackPanel(returnPanel);
      setLoadedTrainingKey(trainingKey);
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      setLoading(false);
    }
  }

  async function ensureRatingLoaded(options = {}) {
    if (!options.force && ratingLoaded) {
      return;
    }

    setLoading(true);
    try {
      const resp = await api.getRating();
      setRating(resp.data.response);
      setRatingLoaded(true);
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      setLoading(false);
    }
  }

  async function ensureDictionaryLoaded(options = {}) {
    if (!options.force && dictionaryLoaded) {
      return;
    }

    if (!options.silent) {
      setLoading(true);
    }
    try {
      const resp = await api.getDictionary();
      setDictionaryWords(ensureArray(resp.data.response));
      setDictionaryLoaded(true);
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!player) {
      return;
    }

    const categoryId = parseIntParam(params?.categoryId);
    const wordId = parseIntParam(params?.wordId);
    const source = searchParams.get('source') || '';
    const categorySearchId = parseIntParam(searchParams.get('category_id'));
    const from = searchParams.get('from');
    const returnTo = searchParams.get('return_to');

    switch (activePanel) {
      case CATEGORY_PANEL:
        if (categoryId > 0) {
          setCategoryActivity(recordCategoryOpened(categoryId));
          void ensureCategoryLoaded(categoryId);
          if (!dictionaryLoaded) {
            void ensureDictionaryLoaded({ silent: true });
          }
        }
        break;
      case WORD_PANEL:
        if (wordId > 0) {
          const backPanel = routeKeys[from] || HOME_PANEL;
          setWordBackPanel(backPanel);
          void ensureWordLoaded(wordId, { backPanel });
        }
        break;
      case TRAINING_PANEL:
        if (source) {
          const returnPanel = routeKeys[returnTo] || HOME_PANEL;
          setTrainingBackPanel(returnPanel);
          void ensureTrainingLoaded(source, categorySearchId, returnPanel);
        }
        break;
      case RATING_PANEL:
        if (!ratingLoaded) {
          void ensureRatingLoaded();
        }
        break;
      case DICTIONARY_PANEL:
        if (!dictionaryLoaded) {
          void ensureDictionaryLoaded();
        }
        break;
      default:
        break;
    }
  }, [
    activePanel,
    player,
    params,
    searchParams,
    categories,
    category,
    loadedCategoryID,
    loadedWordID,
    loadedTrainingKey,
    ratingLoaded,
    dictionaryLoaded
  ]);

  async function openCategory(item) {
    setCategory(item);
    setCategoryWords([]);
    setLoadedCategoryID(0);
    navigateToPath(buildCategoryPath(item.id));
    void ensureCategoryLoaded(item.id, { force: true });
  }

  async function openWord(item, backPanel = CATEGORY_PANEL, options = {}) {
    if (backPanel === CATEGORY_PANEL && options.categoryId) {
      saveCategoryScrollPosition(options.categoryId, window.scrollY || 0);
      setCategoryActivity(recordCategoryWordViewed(options.categoryId, item.id));
    }
    setWordBackPanel(backPanel);
    setWord(item);
    setWordInDictionary(false);
    setLoadedWordID(0);
    setCheckingWordStatus(true);
    navigateToPath(buildWordPath(item.id, {
      from: panelToRouteKey(backPanel),
      categoryId: options.categoryId || 0
    }));
    void ensureWordLoaded(item.id, { force: true, backPanel, previewWord: item });
  }

  async function openRating() {
    await ensureRatingLoaded({ force: true });
    navigateTo(RATING_PANEL);
  }

  async function openDictionary() {
    await ensureDictionaryLoaded({ force: true });
    navigateTo(DICTIONARY_PANEL);
  }

  async function openAchievements() {
    setLoading(true);
    try {
      const resp = await api.getAchievements();
      setAchievements(ensureArray(resp.data.response));
      navigateTo(ACHIEVEMENTS_PANEL);
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      setLoading(false);
    }
  }

  async function startTraining(source, categoryID = 0, returnPanel = HOME_PANEL) {
    const trainingCategory = categories.find((item) => item.id === categoryID) || category;
    if (source === 'category' && trainingCategory?.is_premium && !trainingCategory?.is_purchased) {
      notify('Откройте категорию, чтобы начать тренировку');
      return;
    }
    if (source === 'category' && categoryID > 0) {
      setCategoryActivity(recordCategoryTraining(categoryID));
    }
    await ensureTrainingLoaded(source, categoryID, returnPanel, { force: true });
    navigateToPath(buildTrainingPath({
      source,
      categoryId: categoryID,
      returnTo: panelToRouteKey(returnPanel),
      returnId: returnPanel === CATEGORY_PANEL ? categoryID : 0
    }));
  }

  async function submitTrainingWithAnswers(answers) {
    const correctWordIDs = answers.filter((item) => item.correct).map((item) => item.wordID);
    try {
      setIsSubmittingTraining(true);
      setTrainingSubmitError('');
      await api.completeTraining(correctWordIDs);
      const unlockedResp = await api.checkAchievements('words', 'words_training');
      const achievementsResp = await api.getAchievements();
      setAchievements(ensureArray(achievementsResp.data.response));
      resetTrainingUIState();
      setTraining((prev) => ({
        ...prev,
        done: true,
        correct: correctWordIDs.length,
        unlocked: ensureArray(unlockedResp.data.response)
      }));
    } catch (err) {
      const message = localizeErrorMessage(err?.response?.data?.error?.message || 'Не удалось завершить тренировку');
      setTrainingSubmitError(message);
      notify(message);
    } finally {
      setIsSubmittingTraining(false);
    }
  }

  async function toggleDictionary() {
    if (!word) {
      return;
    }

    await toggleDictionaryWord(word, wordInDictionary, { syncWordPanel: true });
  }

  async function toggleDictionaryWord(wordItem, existsInDictionary, options = {}) {
    if (!wordItem?.id) {
      return;
    }

    try {
      setCheckingWordStatus(true);
      if (existsInDictionary) {
        await api.deleteDictionaryWord(wordItem.id);
        notify('Слово удалено из словаря');
        setDictionaryWords((prev) => prev.filter((item) => item.word.id !== wordItem.id));
        if (options.syncWordPanel) {
          setWordInDictionary(false);
        }
      } else {
        await api.addDictionaryWord(wordItem.id);
        notify('Слово добавлено в словарь');
        setDictionaryWords((prev) => (
          prev.some((item) => item.word.id === wordItem.id)
            ? prev
            : prev.concat([{ word: wordItem, repeat_count: 0 }])
        ));
        if (options.syncWordPanel) {
          setWordInDictionary(true);
        }
      }
      setDictionaryLoaded(true);

      if (wordBackPanel === DICTIONARY_PANEL) {
        const resp = await api.getDictionary();
        setDictionaryWords(ensureArray(resp.data.response));
      }
    } catch (err) {
      notify(localizeErrorMessage(err?.response?.data?.error?.message));
    } finally {
      setCheckingWordStatus(false);
    }
  }

  function answerTraining(option) {
    if (!currentTrainingItem || trainingFeedbackPhase !== 'idle' || isSubmittingTraining) {
      return;
    }

    const correctOption = currentTrainingItem.options.find((item) => item.is_correct)?.option || '';
    const nextAnswers = training.answers.concat([{
      key: `${currentTrainingItem.word_id}-${training.answers.length}`,
      option: option.option,
      correct: option.is_correct,
      wordID: currentTrainingItem.word_id,
      prompt: currentTrainingItem.word,
      correctOption,
      translationType: currentTrainingItem.translation_type
    }]);

    setSelectedTrainingOption(option.option);
    setTrainingFeedbackAnswer({
      option: option.option,
      correct: option.is_correct,
      correctOption
    });
    setTrainingFeedbackPhase(option.is_correct ? 'correct' : 'incorrect');
  }

  function advanceTraining() {
    if (!currentTrainingItem || !trainingFeedbackAnswer || isSubmittingTraining) {
      return;
    }

    const correctOption = currentTrainingItem.options.find((item) => item.is_correct)?.option || '';
    const nextAnswers = training.answers.concat([{
      key: `${currentTrainingItem.word_id}-${training.answers.length}`,
      option: trainingFeedbackAnswer.option,
      correct: trainingFeedbackAnswer.correct,
      wordID: currentTrainingItem.word_id,
      prompt: currentTrainingItem.word,
      correctOption,
      translationType: currentTrainingItem.translation_type
    }]);

    setTraining((prev) => ({
      ...prev,
      answers: nextAnswers
    }));
    setSelectedTrainingOption('');
    setTrainingFeedbackPhase('idle');
    setTrainingFeedbackAnswer(null);

    if (nextAnswers.length === training.items.length) {
      void submitTrainingWithAnswers(nextAnswers);
    }
  }

  async function restartTraining() {
    if (!training.source) {
      return;
    }

    await startTraining(training.source, training.category_id, trainingBackPanel);
  }

  function retryTrainingSubmit() {
    if (training.answers.length === 0) {
      return;
    }

    void submitTrainingWithAnswers(training.answers);
  }

  function normalizePaymentError(error) {
    if (error?.error_data?.error_code === 3) {
      return 'Недостаточно голосов для покупки';
    }
    if (error?.error_data?.error_code === 4 || error?.error_data?.error_reason === 'user_denied') {
      return 'Покупка отменена';
    }
    if (String(error?.message || '').toLowerCase().includes('timeout')) {
      return 'Превышено время ожидания оплаты';
    }
    return error?.message || 'Не удалось выполнить покупку';
  }

  async function purchaseCategory(categoryItem) {
    if (!categoryItem?.vk_item_id) {
      notify('Категория недоступна для покупки');
      return;
    }
    if (!vkBridge.supports('VKWebAppShowOrderBox')) {
      notify('Покупки недоступны в этой версии приложения');
      return;
    }

    setLoading(true);
    try {
      const result = await Promise.race([
        vkBridge.send('VKWebAppShowOrderBox', {
          type: 'item',
          item: categoryItem.vk_item_id
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Payment timeout')), 60000))
      ]);
      if (!result?.success) {
        notify('Статус покупки не подтверждён. Проверьте категорию через несколько минут.');
        return;
      }
      const categoriesResp = await api.getCategories();
      const nextCategories = ensureArray(categoriesResp.data.response);
      const updatedCategory = nextCategories.find((item) => item.id === categoryItem.id) || categoryItem;
      setCategories(nextCategories);
      setCategory(updatedCategory);
      setLoadedCategoryID(0);
      await ensureCategoryLoaded(categoryItem.id, { force: true, silent: true, categoryOverride: updatedCategory });
      notify('Категория открыта');
    } catch (err) {
      notify(normalizePaymentError(err));
    } finally {
      setLoading(false);
    }
  }

  if (loading && !player && !startupError) {
    return (
      <SplitLayout>
        <SplitCol>
          <AppShell>
            <div className="flex min-h-[60vh] items-center justify-center">
              <Spinner size="large" />
            </div>
          </AppShell>
        </SplitCol>
      </SplitLayout>
    );
  }

  if (!loading && !player) {
    return (
      <SplitLayout>
        <SplitCol>
          <AppShell>
            <div className="mx-auto mt-[60px] max-w-[420px] text-center">
              <h1 className="mb-4 text-[22px] font-bold">Что-то пошло не так</h1>
              <p className="mb-[18px] leading-[1.5]">{startupError || 'Ошибка стартовой загрузки'}</p>
              <PrimaryButton onClick={bootstrap} secondary>Обновить</PrimaryButton>
            </div>
          </AppShell>
        </SplitCol>
      </SplitLayout>
    );
  }

  return (
    <SplitLayout>
      <SplitCol>
        <Root activeView={DEFAULT_VIEW}>
          <View nav={DEFAULT_VIEW} activePanel={activePanel}>
            <HomePanel
              nav={HOME_PANEL}
              player={player}
              categories={categories}
              categoryActivity={categoryActivity}
              currentUserRating={currentUserRating}
              navigateTo={navigateTo}
              openRating={openRating}
              openCategory={openCategory}
            />

            <CategoryPanel
              nav={CATEGORY_PANEL}
              category={category}
              categoryActivity={categoryActivity}
              categoryWords={categoryWords}
              dictionaryWordIDs={new Set(dictionaryWords.map((item) => item.word.id))}
              loading={loading}
              onBack={() => goBack(resolvePanelPath(HOME_PANEL))}
              openWord={openWord}
              startTraining={startTraining}
              purchaseCategory={purchaseCategory}
              toggleDictionaryWord={toggleDictionaryWord}
            />

            <WordPanel
              nav={WORD_PANEL}
              word={word}
              wordInDictionary={wordInDictionary}
              checkingWordStatus={checkingWordStatus}
              onBack={() => goBack(resolveWordFallbackPath(searchParams))}
              toggleDictionary={toggleDictionary}
            />

            <DailyPanel
              nav={DAILY_PANEL}
              dailyWords={dailyWords}
              loading={loading}
              onBack={() => goBack(resolvePanelPath(HOME_PANEL))}
              openWord={openWord}
              startTraining={startTraining}
            />

            <TrainingPanel
              nav={TRAINING_PANEL}
              training={training}
              currentTrainingIndex={currentTrainingIndex}
              currentTrainingItem={currentTrainingItem}
              loading={loading}
              selectedTrainingOption={selectedTrainingOption}
              trainingFeedbackPhase={trainingFeedbackPhase}
              trainingFeedbackAnswer={trainingFeedbackAnswer}
              isSubmittingTraining={isSubmittingTraining}
              trainingSubmitError={trainingSubmitError}
              onBack={() => goBack(resolveTrainingFallbackPath(searchParams))}
              answerTraining={answerTraining}
              advanceTraining={advanceTraining}
              onRestart={restartTraining}
              onRetrySubmit={retryTrainingSubmit}
            />

            <RatingPanel
              nav={RATING_PANEL}
              rating={rating}
              loading={loading}
              currentUserRating={currentUserRating}
              currentPlayerID={player?.vk_user_id}
              onBack={() => goBack(resolvePanelPath(HOME_PANEL))}
            />

            <CabinetPanel
              nav={CABINET_PANEL}
              onBack={() => goBack(resolvePanelPath(HOME_PANEL))}
              openAchievements={openAchievements}
              openDictionary={openDictionary}
            />

            <DictionaryPanel
              nav={DICTIONARY_PANEL}
              dictionaryWords={dictionaryWords}
              loading={loading}
              onBack={() => goBack(resolvePanelPath(CABINET_PANEL))}
              openWord={openWord}
              startTraining={startTraining}
            />

            <AchievementsPanel
              nav={ACHIEVEMENTS_PANEL}
              achievements={achievements}
              loading={loading}
              onBack={() => goBack(resolvePanelPath(CABINET_PANEL))}
              selectAchievement={setSelectedAchievement}
            />
          </View>
        </Root>

        {selectedAchievement ? <AchievementModal achievement={selectedAchievement} onClose={() => setSelectedAchievement(null)} /> : null}
        {toast ? <Snackbar onClose={() => setToast('')}>{toast}</Snackbar> : null}
        {loading && player ? <LoadingBlock /> : null}
      </SplitCol>
    </SplitLayout>
  );
}

export default App;
